import { createAction, props } from '@ngrx/store';

import {
  CandidateProcedure,
  CandidateProcedureDecision,
  CandidateVerification,
  CandidateVerificationHistory,
  NewCandidateVerificationOpts,
  PaginatedResponse,
  RequestCandidateVerificationSupplementaryInfoOpts,
  SaveCandidateVerificationResultOpts,
  SearchCandidateVerificationsOpts,
  ServiceTypeOptional,
  SmsSendResult,
  UpdateCandidateVerificationDecisionOpts,
  UpdateCandidateVerificationDetailsOpts,
  UpdateCandidateVerificationStatusOpts,
} from '@interfaces';

export const searchCandidateVerifications = createAction(
  '[Candidate Verification Management] Search Candidate Verifications',
  props<{ opts: SearchCandidateVerificationsOpts }>()
);
export const searchCandidateVerificationsSuccess = createAction(
  '[Candidate Verification Management] Search Candidate Verifications Success',
  props<{ candidateVerifications: PaginatedResponse<Partial<CandidateVerification>> }>()
);
export const searchCandidateVerificationsError = createAction(
  '[Candidate Verification Management] Search Candidate Verifications Error',
  props<{ errorMessage: any }>()
);

export const getCandidateVerificationDetails = createAction(
  '[Candidate Verification Management] Get Candidate Verification Details',
  props<{ candidateVerificationId: string }>()
);
export const getCandidateVerificationDetailsSuccess = createAction(
  '[Candidate Verification Management] Get Candidate Verification Details Success',
  props<{ candidateVerification: CandidateVerification }>()
);
export const getCandidateVerificationDetailsError = createAction(
  '[Candidate Verification Management] Get Candidate Verification Details Error',
  props<{ errorMessage: any }>()
);

export const getCandidateVerificationHistory = createAction(
  '[Candidate Verification Management] Get Candidate Verification History',
  props<{ candidateVerificationId: string }>()
);
export const getCandidateVerificationHistorySuccess = createAction(
  '[Candidate Verification Management] Get Candidate Verification History Success',
  props<{ candidateVerificationHistory: CandidateVerificationHistory[] }>()
);
export const getCandidateVerificationHistoryError = createAction(
  '[Candidate Verification Management] Get Candidate Verification History Error',
  props<{ errorMessage: any }>()
);

export const addNewCandidateVerification = createAction(
  '[Candidate Verification Management] Add New Candidate Verification',
  props<{ opts: NewCandidateVerificationOpts }>()
);
export const addNewCandidateVerificationSuccess = createAction(
  '[Candidate Verification Management] Add New Candidate Verification Success',
  props<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }>()
);
export const addNewCandidateVerificationError = createAction(
  '[Candidate Verification Management] Add New Candidate Verification Error',
  props<{ errorMessage: any }>()
);

export const updateCandidateVerificationBasicDetails = createAction(
  '[Candidate Verification Management] Update Candidate Verification Basic Details',
  props<{ candidateVerificationId: string; opts: UpdateCandidateVerificationDetailsOpts }>()
);
export const updateCandidateVerificationBasicDetailsSuccess = createAction(
  '[Candidate Verification Management] Update Candidate Verification Basic Details Success',
  props<{ candidateVerification: CandidateVerification; smsResult?: SmsSendResult | null }>()
);
export const updateCandidateVerificationBasicDetailsError = createAction(
  '[Candidate Verification Management] Update Candidate Verification Basic Details Error',
  props<{ errorMessage: any }>()
);

export const updateCandidateVerificationStatus = createAction(
  '[Candidate Verification Management] Update Candidate Verification Status',
  props<{ candidateVerificationId: string; opts: UpdateCandidateVerificationStatusOpts }>()
);
export const updateCandidateVerificationStatusSuccess = createAction(
  '[Candidate Verification Management] Update Candidate Verification Status Success',
  props<{ candidateVerification: Partial<CandidateVerification> }>()
);
export const updateCandidateVerificationStatusError = createAction(
  '[Candidate Verification Management] Update Candidate Verification Status Error',
  props<{ errorMessage: any }>()
);

export const saveCandidateVerificationResult = createAction(
  '[Candidate Verification Management] Save Candidate Verification Result',
  props<{ candidateVerificationId: string; opts: SaveCandidateVerificationResultOpts }>()
);
export const saveCandidateVerificationResultSuccess = createAction(
  '[Candidate Verification Management] Save Candidate Verification Result Success',
  props<{ candidateVerification: CandidateVerification }>()
);
export const saveCandidateVerificationResultError = createAction(
  '[Candidate Verification Management] Save Candidate Verification Result Error',
  props<{ errorMessage: any }>()
);

export const setCandidateVerificationDecision = createAction(
  '[Candidate Verification Management] Set Candidate Verification Decision',
  props<{ candidateVerificationId: string; opts: UpdateCandidateVerificationDecisionOpts }>()
);
export const setCandidateVerificationDecisionSuccess = createAction(
  '[Candidate Verification Management] Set Candidate Verification Decision Success',
  props<{ candidateVerification: CandidateVerification }>()
);
export const setCandidateVerificationDecisionError = createAction(
  '[Candidate Verification Management] Set Candidate Verification Decision Error',
  props<{ candidateVerificationId: string; opts: UpdateCandidateVerificationDecisionOpts; errorMessage: any }>()
);

export const updateCandidateVerificationProcedureDecision = createAction(
  '[Candidate Verification Management] Update Candidate Verification Procedure Decision',
  props<{ candidateVerificationId: string; procedureId: string; decision: CandidateProcedureDecision }>()
);
export const updateCandidateVerificationProcedureDecisionSuccess = createAction(
  '[Candidate Verification Management] Update Candidate Verification Procedure Decision Success',
  props<{
    candidateVerificationId: string;
    procedureId: string;
    decision: CandidateProcedureDecision;
    candidateVerification: CandidateVerification;
  }>()
);
export const updateCandidateVerificationProcedureDecisionError = createAction(
  '[Candidate Verification Management] Update Candidate Verification Procedure Decision Error',
  props<{
    candidateVerificationId: string;
    procedureId: string;
    decision: CandidateProcedureDecision;
    errorMessage: any;
  }>()
);

export const addCandidateVerificationExtraService = createAction(
  '[Candidate Verification Management] Add Candidate Verification Extra Service',
  props<{ candidateVerificationId: string; serviceType: ServiceTypeOptional }>()
);
export const addCandidateVerificationExtraServiceSuccess = createAction(
  '[Candidate Verification Management] Add Candidate Verification Extra Service Success',
  props<{
    candidateVerificationId: string;
    serviceType: ServiceTypeOptional;
    candidateVerification: CandidateVerification;
  }>()
);
export const addCandidateVerificationExtraServiceError = createAction(
  '[Candidate Verification Management] Add Candidate Verification Extra Service Error',
  props<{ candidateVerificationId: string; serviceType: ServiceTypeOptional; errorMessage: any }>()
);

export const requestCandidateVerificationSupplementaryInfo = createAction(
  '[Candidate Verification Management] Request Candidate Verification Supplementary Info',
  props<{ candidateVerificationId: string; opts: RequestCandidateVerificationSupplementaryInfoOpts }>()
);
export const requestCandidateVerificationSupplementaryInfoSuccess = createAction(
  '[Candidate Verification Management] Request Candidate Verification Supplementary Info Success',
  props<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }>()
);
export const requestCandidateVerificationSupplementaryInfoError = createAction(
  '[Candidate Verification Management] Request Candidate Verification Supplementary Info Error',
  props<{ errorMessage: any }>()
);

export const regenerateCandidateFormLinkAndPin = createAction(
  '[Candidate Verification Management] Regenerate Candidate Form Link And PIN',
  props<{ candidateVerificationId: string }>()
);
export const regenerateCandidateFormLinkAndPinSuccess = createAction(
  '[Candidate Verification Management] Regenerate Candidate Form Link And PIN Success',
  props<{ candidateVerification: CandidateVerification; smsResult: SmsSendResult }>()
);
export const regenerateCandidateFormLinkAndPinError = createAction(
  '[Candidate Verification Management] Regenerate Candidate Form Link And PIN Error',
  props<{ errorMessage: any }>()
);

export const extendCandidateVerificationFormValidity = createAction(
  '[Candidate Verification Management] Extend Candidate Verification Form Validity',
  props<{ candidateVerificationId: string; extendByDays: number }>()
);
export const extendCandidateVerificationFormValiditySuccess = createAction(
  '[Candidate Verification Management] Extend Candidate Verification Form Validity Success',
  props<{ candidateVerification: Partial<CandidateVerification>; smsResult: SmsSendResult }>()
);
export const extendCandidateVerificationFormValidityError = createAction(
  '[Candidate Verification Management] Extend Candidate Verification Form Validity Error',
  props<{ errorMessage: any }>()
);

export const deleteCandidateVerification = createAction(
  '[Candidate Verification Management] Delete Candidate Verification',
  props<{ candidateVerificationId: string }>()
);
export const deleteCandidateVerificationSuccess = createAction(
  '[Candidate Verification Management] Delete Candidate Verification Success',
  props<{ deletedCandidateVerificationId: string }>()
);
export const deleteCandidateVerificationError = createAction(
  '[Candidate Verification Management] Delete Candidate Verification Error',
  props<{ errorMessage: any }>()
);

export const openCandidateVerificationFile = createAction(
  '[Candidate Verification Management] Open Candidate Verification File',
  props<{ candidateVerificationId: string; fileId: string; previewTargetKey?: string }>()
);
export const openCandidateVerificationFileSuccess = createAction(
  '[Candidate Verification Management] Open Candidate Verification File Success',
  props<{ fileId: string }>()
);
export const openCandidateVerificationFileError = createAction(
  '[Candidate Verification Management] Open Candidate Verification File Error',
  props<{ fileId: string; errorMessage: any }>()
);
